import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Mail, DollarSign, Eye, MousePointerClick, MessageSquare, 
  ArrowRight, Clock, Loader2, MailOpen, CheckCircle2, UserPlus
} from 'lucide-react';
import { getAuthToken } from '../../utils/auth';
import { formatUsPhone } from '../../utils/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityFeed = ({ leads, isDark, onLeadClick, onCopyLink, onCreateTestLead }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    generateActivities();
  }, [leads]);

  const generateActivities = () => {
    setIsLoading(true);
    
    // Generate activities from leads data
    const allActivities = [];

    leads.forEach(lead => {
      // New lead activity
      allActivities.push({
        id: `lead-${lead.id}`,
        type: 'new_lead',
        icon: UserPlus,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        title: 'New Lead',
        description: `${lead.name || 'Anonymous'} completed the match form`,
        meta: lead.rv_type ? `Looking for: ${lead.rv_type}` : null,
        timestamp: lead.created_at,
        leadId: lead.id,
      });

      // Page views
      (lead.page_views || []).forEach((pv, idx) => {
        allActivities.push({
          id: `pv-${lead.id}-${idx}`,
          type: 'page_view',
          icon: Eye,
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Page View',
          description: `${lead.name || lead.email} viewed their matches`,
          timestamp: pv.timestamp,
          leadId: lead.id,
        });
      });

      // Click tracking
      (lead.click_tracking || []).forEach((click, idx) => {
        allActivities.push({
          id: `click-${lead.id}-${idx}`,
          type: 'click',
          icon: MousePointerClick,
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          title: 'RV Click',
          description: `${lead.name || lead.email} clicked on ${click.rv_year} ${click.rv_brand} ${click.rv_model}`,
          meta: click.source === 'email' ? 'From email link' : 'From matches page',
          timestamp: click.timestamp,
          leadId: lead.id,
        });
      });
    });

    // Sort by timestamp (most recent first)
    allActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    setActivities(allActivities.slice(0, 50)); // Limit to 50 activities
    setIsLoading(false);
  };

  const formatRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'new_lead', label: 'New Leads' },
    { id: 'click', label: 'Clicks' },
    { id: 'page_view', label: 'Views' },
  ];

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities.filter(a => a.type === filter);

  if (isLoading) {
    return (
      <div className="rounded-2xl p-6 bg-white/[0.03] border border-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-amber-400" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 backdrop-blur-sm"
      data-testid="activity-feed"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-white">
            Activity Feed
          </h3>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.id
                  ? 'bg-[#F2A900] text-black'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredActivities.length === 0 ? (
          <div className="px-5 py-8 text-center" data-testid="activity-empty">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 ${
              isDark ? 'bg-white/5 text-slate-300' : 'bg-gray-100 text-gray-500'
            }`}>
              <Clock size={22} />
            </div>
            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No activity yet.</p>
            <p className={`text-xs mt-1 max-w-xs mx-auto ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              Share your form link or create a test lead to see the complete advisor workflow.
            </p>
            {(onCopyLink || onCreateTestLead) && (
              <div className="mt-3 flex flex-wrap gap-2 justify-center">
                {onCopyLink && (
                  <button
                    type="button"
                    onClick={onCopyLink}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
                      isDark ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                    data-testid="activity-empty-copy-link"
                  >
                    Copy Form Link
                  </button>
                )}
                {onCreateTestLead && (
                  <button
                    type="button"
                    onClick={onCreateTestLead}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                      isDark ? 'border-white/15 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid="activity-empty-test-lead"
                  >
                    Create Test Lead
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredActivities.map((activity) => {
              const Icon = activity.icon;
              return (
                <button
                  key={activity.id}
                  onClick={() => activity.leadId && onLeadClick && onLeadClick(activity.leadId)}
                  className="w-full flex items-start gap-3 px-5 py-3 text-left transition-colors hover:bg-white/5"
                  data-testid={`activity-${activity.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={16} className={activity.iconColor.replace('-600', '-300')} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">
                      {activity.title}
                    </p>
                    <p className="text-xs truncate text-slate-400">
                      {activity.description}
                    </p>
                    {activity.meta && (
                      <p className="text-xs mt-0.5 text-slate-500">
                        {activity.meta}
                      </p>
                    )}
                  </div>
                  <span className="text-xs shrink-0 text-slate-500">
                    {formatRelativeTime(activity.timestamp)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredActivities.length > 10 && (
        <div className="px-5 py-3 text-center border-t border-white/10">
          <p className="text-xs text-slate-500">
            Showing {filteredActivities.length} activities
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
